import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import type { User } from '../../../types';
import { searchChangeFormUsers } from '../../../utils/searchUsers';

/**
 * Usuários para Dono/Solicitante quando o campo não usa pesquisa no servidor (select clássico).
 */
export function useChangeFormUsers(options?: { enabled?: boolean; limit?: number }) {
  const { user } = useAuth();
  const enabled = options?.enabled ?? true;
  const limit = options?.limit ?? 50;
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const items = await searchChangeFormUsers('', localStorage.getItem('token'), {
        limit,
        signal,
      });
      if (!signal?.aborted) setUsers(items);
    } catch {
      if (!signal?.aborted) setUsers([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [enabled, load]);

  const withCurrentUser = useMemo(() => {
    if (!user?.id) return users;
    if (users.some((entry) => entry.id === user.id)) return users;
    const self = {
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      is_active: true,
    } as User;
    return [self, ...users];
  }, [user, users]);

  return { users: withCurrentUser, loading, reload: load };
}
